import { type MouseEvent, type ReactNode, memo } from "react";

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from "@mui/icons-material";
import { IconButton, SxProps, Theme } from "@mui/material";

import { theme } from "@/src/theme/theme";

interface CarouselArrowProps {
  direction: "left" | "right";
  onClick: (e: MouseEvent) => void;
  icon?: ReactNode;
  isVisible?: boolean;
  disabled?: boolean;
  iconSize?: number;
  sx?: SxProps<Theme>;
}

const CarouselArrow = ({
  direction,
  onClick,
  icon,
  isVisible = true,
  disabled = false,
  iconSize,
  sx
}: CarouselArrowProps) => {
  const isLeft = direction === "left";

  return (
    <IconButton
      disableRipple
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
      sx={{
        position: "absolute",
        [isLeft ? "left" : "right"]: 0,
        top: "50%",
        transform: "translateY(-50%)",
        width: 33,
        height: 72,
        backgroundColor: theme.palette.text.secondary,
        color: theme.palette.common.white,
        zIndex: 25,
        borderRadius: isLeft ? "0 8px 8px 0" : "8px 0 0 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "opacity 0.2s ease",
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        outline: "none",
        "&:focus": {
          outline: "none"
        },
        "&:hover": {
          backgroundColor: theme.palette.text.primary
        },
        ...sx
      }}
      aria-label={isLeft ? "Previous image" : "Next image"}
    >
      {icon ??
        (isLeft ? (
          <ChevronLeftIcon sx={iconSize ? { fontSize: iconSize } : undefined} />
        ) : (
          <ChevronRightIcon
            sx={iconSize ? { fontSize: iconSize } : undefined}
          />
        ))}
    </IconButton>
  );
};

export default memo(CarouselArrow);
